import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInboundFeatureFlag } from '@/hooks/useInboundFeatureFlag';
import { useClients } from '@/hooks/useClients';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

import { useGroupedActions } from '@/hooks/useGroupedActions';
import { ContactCard } from '@/components/ContactCard';
import { useOptimizedSupabaseProspectLists } from '@/hooks/useOptimizedSupabaseProspectLists';
import { ArchiveConfirmationModal } from '@/components/ArchiveConfirmationModal';
import {
  CheckCircle,
  PhoneIncoming,
  PhoneOutgoing,
  Search,
  Archive,
  PlayCircle,
  RefreshCw,
} from 'lucide-react';
import { TrainingVideoModal } from '@/components/TrainingVideoModal';
import { useLocationTrainingVideo } from '@/hooks/useLocationTrainingVideo';

import { useDebouncedCallback } from 'use-debounce';
import { getActionDeadlineDate } from '@/lib/time';
import { extractContactIntelligence } from '@/utils/contactIntelligence';

interface CallAction {
  id: string;
  call_id: string;
  campaign_id: string;
  prospect_id: string;
  type: string;
  confidence: number;
  phrase: string | null;
  action_data: any;
  processed: boolean;
  created_at: string;
  status?: string;
  completed_at?: string;
  archived_at?: string;
  archived_until?: string;
  campaign?: {
    name: string;
    type?: string;
    businessContext?: {
      company: string;
      industry: string;
      context: string;
    };
    client_id?: string;
    client_name?: string;
  };
  // Inbound-specific fields
  mode?: string;
  caller_name?: string;
  caller_phone?: string;
  target_person_dept?: string;
  snippet?: string;
}

interface ActionsProps {
  isClientPortal?: boolean;
  clientId?: string;
  clientName?: string;
  branding?: any;
}

export default function Actions({
  isClientPortal = false,
  clientId,
  clientName,
  branding,
}: ActionsProps = {}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { enableInboundMode } = useInboundFeatureFlag();
  const [actions, setActions] = useState<CallAction[]>([]);
  const [archivedActions, setArchivedActions] = useState<CallAction[]>([]);
  const [viewMode, setViewMode] = useState<'active' | 'archive'>('active');
  const [modeFilter, setModeFilter] = useState<'all' | 'outbound' | 'inbound'>(
    'all',
  );
  const [clientFilter, setClientFilter] = useState<string>(
    isClientPortal && clientId ? clientId : 'all',
  );

  // Ensure clientFilter is locked to clientId in client portal mode
  useEffect(() => {
    if (isClientPortal && clientId && clientFilter !== clientId) {
      setClientFilter(clientId);
    }
  }, [isClientPortal, clientId, clientFilter]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState<'due_date' | 'created_at'>(
    'due_date',
  );
  const { hasVideo, displayTitle } = useLocationTrainingVideo('actions');
  const [showProcessed, setShowProcessed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const { clients } = useClients();
  const { prospectLists } = useOptimizedSupabaseProspectLists();
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [contactToArchive, setContactToArchive] = useState<{
    contactKey: string;
    name?: string;
    actionIds: string[];
  } | null>(null);

  // Pagination state for call_ids
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreCalls, setHasMoreCalls] = useState(true);
  const CALLS_PER_PAGE = 200;

  // Helper: page through call_actions (ordered by latest action) to collect DISTINCT call_ids
  const PAGE_CHUNK = 1000;
  const getDistinctCallIdsPage = async (pageIndex: number) => {
    const skipDistinct = pageIndex * CALLS_PER_PAGE;
    const pageChunk = PAGE_CHUNK;
    let offset = 0;
    const seen = new Set<string>();
    const collected: string[] = [];
    let skipped = 0;

    while (collected.length < CALLS_PER_PAGE) {
      let query = supabase
        .from('call_actions')
        .select(
          'call_id, created_at, campaigns!left(client_id, campaign_type, name)',
        )
        .order('created_at', { ascending: false })
        .range(offset, offset + pageChunk - 1);

      // In client portal mode, don't filter by user_id since call_actions belong to admin
      // Instead, we'll filter by client_id later
      if (!isClientPortal) {
        query = query.eq('user_id', user?.id);
      }

      // Status filter aligned with viewMode
      if (viewMode === 'archive') {
        query = query.eq('status', 'archived');
      } else {
        query = query.eq('status', 'active');
      }

      // Client filter (optional server-side)
      if (clientFilter !== 'all') {
        if (clientFilter === 'unassigned') {
          query = query.is('campaigns.client_id', null);
        } else {
          query = query.eq('campaigns.client_id', clientFilter);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) break;

      for (const row of data as any[]) {
        const cid = row.call_id as string | null;
        if (!cid || seen.has(cid)) continue;
        seen.add(cid);
        if (skipped < skipDistinct) {
          skipped++;
          continue;
        }
        collected.push(cid);
        if (collected.length >= CALLS_PER_PAGE) break;
      }

      if (data.length < pageChunk) break;
      offset += pageChunk;
    }

    const hasMore = collected.length === CALLS_PER_PAGE;
    return { callIds: collected, hasMore };
  };

  // Debounce toggle action to prevent rapid API calls
  const debouncedToggleActionProcessed = useDebouncedCallback(
    async (actionId: string) => {
      try {
        const action = actions.find((a) => a.id === actionId);
        if (!action) return;

        const { error } = await supabase
          .from('call_actions')
          .update({
            processed: !action.processed,
            processed_by: action.processed ? null : user?.id,
          })
          .eq('id', actionId);

        if (error) throw error;

        // Update local state without removing the action
        setActions((prev) =>
          prev.map((a) =>
            a.id === actionId ? { ...a, processed: !a.processed } : a,
          ),
        );

        toast({
          title: 'Success',
          description: `Action ${
            action.processed ? 'unmarked' : 'marked'
          } as done`,
        });
      } catch (error) {
        console.error('Error toggling action status:', error);
        toast({
          title: 'Error',
          description: 'Failed to update action status',
          variant: 'destructive',
        });
      }
    },
    300,
  );

  useEffect(() => {
    if (user) {
      // Load action type labels for outbound actions
      import('../utils/actionTypesRegistry').then(
        ({ loadActionTypeLabels }) => {
          loadActionTypeLabels('outbound');
        },
      );

      fetchActions();
    }
  }, [user, viewMode, clientFilter]);

  const fetchActions = async (resetPagination = true) => {
    try {
      if (resetPagination) {
        setCurrentPage(0);
        setHasMoreCalls(true);
      }

      // Step 1: Page through call_actions to collect DISTINCT call_ids by most recent action
      const { callIds: uniqueCallIds, hasMore } = await getDistinctCallIdsPage(
        resetPagination ? 0 : currentPage,
      );

      if (!uniqueCallIds || uniqueCallIds.length === 0) {
        setActions([]);
        setArchivedActions([]);
        setHasMoreCalls(false);
        if (resetPagination) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
        return;
      }

      setHasMoreCalls(hasMore);

      // Step 2: Fetch ALL actions for these specific call_ids in safe chunks to avoid row limits
      const chunkSize = 50;
      let allData: any[] = [];
      for (let i = 0; i < uniqueCallIds.length; i += chunkSize) {
        const chunk = uniqueCallIds.slice(i, i + chunkSize);
        let chunkQuery = supabase
          .from('call_actions')
          .select(
            `
            *,
            campaigns(
              name,
              campaign_type,
              script_id,
              inbound_script_id,
              outbound_script_id,
              client_id,
              clients(
                id,
                name
              )
            )
          `,
          )
          .in('call_id', chunk)
          .order('created_at', { ascending: false });

        // In client portal mode, don't filter by user_id since call_actions belong to admin
        if (!isClientPortal) {
          chunkQuery = chunkQuery.eq('user_id', user?.id);
        }

        const { data: chunkData, error: chunkError } = await chunkQuery;
        if (chunkError) throw chunkError;
        allData = allData.concat(chunkData || []);
      }
      const data = allData;

      // Fetch script metadata for business context
      const scriptIds = new Set<string>();
      (data || []).forEach((item: any) => {
        if (item.campaigns?.script_id) scriptIds.add(item.campaigns.script_id);
        if (item.campaigns?.inbound_script_id)
          scriptIds.add(item.campaigns.inbound_script_id);
        if (item.campaigns?.outbound_script_id)
          scriptIds.add(item.campaigns.outbound_script_id);
      });

      let scriptsData: any[] = [];
      if (scriptIds.size > 0) {
        const { data: scripts } = await supabase
          .from('scripts')
          .select(
            'id, company_name, target_industry, target_audience, call_goal, item_sold',
          )
          .in('id', Array.from(scriptIds));
        scriptsData = scripts || [];
      }

      // Transform the data to match our interface with business context
      const transformedData = (data || []).map((item: any) => {
        const campaign = item.campaigns;
        let businessContext = { company: '', industry: '', context: '' };

        if (campaign) {
          const relevantScriptId =
            campaign.campaign_type === 'inbound'
              ? campaign.inbound_script_id || campaign.script_id
              : campaign.outbound_script_id || campaign.script_id;

          const script = scriptsData.find((s) => s.id === relevantScriptId);

          if (script) {
            businessContext = {
              company: script.company_name || '',
              industry: script.target_industry || script.target_audience || '',
              context:
                campaign.campaign_type === 'inbound'
                  ? extractInboundContext(item.phrase, item.action_data)
                  : script.call_goal || script.item_sold || '',
            };
          }
        }

        return {
          ...item,
          campaign: {
            name: campaign?.name || 'Unknown Campaign',
            type: campaign?.campaign_type || 'outbound',
            businessContext,
            client_id: campaign?.client_id,
            client_name: campaign?.clients?.name,
          },
          // Map action_data.mode to top-level mode for proper filtering
          mode: item.action_data?.mode || item.mode || 'outbound',
        };
      });

      // Separate active and archived actions
      const activeActions = transformedData.filter(
        (action) => action.status !== 'archived',
      );
      const archivedActionsData = transformedData.filter(
        (action) => action.status === 'archived',
      );

      if (resetPagination) {
        setActions(activeActions);
        setArchivedActions(archivedActionsData);
      } else {
        // Append new actions for "Load More" functionality
        setActions((prev) => {
          const existingIds = new Set(prev.map((a) => a.id));
          const newActions = activeActions.filter(
            (a) => !existingIds.has(a.id),
          );
          return [...prev, ...newActions];
        });
        setArchivedActions((prev) => {
          const existingIds = new Set(prev.map((a) => a.id));
          const newArchivedActions = archivedActionsData.filter(
            (a) => !existingIds.has(a.id),
          );
          return [...prev, ...newArchivedActions];
        });
      }
    } catch (error) {
      console.error('Error fetching actions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load actions',
        variant: 'destructive',
      });
    } finally {
      if (resetPagination) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const loadMoreActions = async () => {
    if (loadingMore || !hasMoreCalls) return;

    setLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);

    try {
      // Fetch next page of DISTINCT calls from call_actions based on latest action
      const { callIds: uniqueCallIds, hasMore } =
        await getDistinctCallIdsPage(nextPage);

      if (!uniqueCallIds || uniqueCallIds.length === 0) {
        setHasMoreCalls(false);
        setLoadingMore(false);
        return;
      }

      setHasMoreCalls(hasMore);

      // Fetch ALL actions for these call_ids in chunks
      const chunkSize = 50;
      let allData: any[] = [];
      for (let i = 0; i < uniqueCallIds.length; i += chunkSize) {
        const chunk = uniqueCallIds.slice(i, i + chunkSize);
        let chunkQuery = supabase
          .from('call_actions')
          .select(
            `
            *,
            campaigns(
              name,
              campaign_type,
              script_id,
              inbound_script_id,
              outbound_script_id,
              client_id,
              clients(
                id,
                name
              )
            )
          `,
          )
          .in('call_id', chunk)
          .order('created_at', { ascending: false });

        // In client portal mode, don't filter by user_id since call_actions belong to admin
        if (!isClientPortal) {
          chunkQuery = chunkQuery.eq('user_id', user?.id);
        }

        const { data: chunkData, error: chunkError } = await chunkQuery;
        if (chunkError) throw chunkError;
        allData = allData.concat(chunkData || []);
      }
      const data = allData;

      // if (process.env.NODE_ENV === 'development') {
      //   console.debug(
      //     `[Actions] distinctCalls=${uniqueCallIds.length}, actionsFetched=${data.length}`
      //   );
      // }

      // Process the data similar to fetchActions
      const scriptIds = new Set<string>();
      (data || []).forEach((item: any) => {
        if (item.campaigns?.script_id) scriptIds.add(item.campaigns.script_id);
        if (item.campaigns?.inbound_script_id)
          scriptIds.add(item.campaigns.inbound_script_id);
        if (item.campaigns?.outbound_script_id)
          scriptIds.add(item.campaigns.outbound_script_id);
      });

      let scriptsData: any[] = [];
      if (scriptIds.size > 0) {
        const { data: scripts } = await supabase
          .from('scripts')
          .select(
            'id, company_name, target_industry, target_audience, call_goal, item_sold',
          )
          .in('id', Array.from(scriptIds));
        scriptsData = scripts || [];
      }

      // Transform the data to match our interface with business context
      const transformedData = (data || []).map((item: any) => {
        const campaign = item.campaigns;
        let businessContext = { company: '', industry: '', context: '' };

        if (campaign) {
          const relevantScriptId =
            campaign.campaign_type === 'inbound'
              ? campaign.inbound_script_id || campaign.script_id
              : campaign.outbound_script_id || campaign.script_id;

          const script = scriptsData.find((s) => s.id === relevantScriptId);

          if (script) {
            businessContext = {
              company: script.company_name || '',
              industry: script.target_industry || script.target_audience || '',
              context:
                campaign.campaign_type === 'inbound'
                  ? extractInboundContext(item.phrase, item.action_data)
                  : script.call_goal || script.item_sold || '',
            };
          }
        }

        return {
          ...item,
          campaign: {
            name: campaign?.name || 'Unknown Campaign',
            type: campaign?.campaign_type || 'outbound',
            businessContext,
            client_id: campaign?.client_id,
            client_name: campaign?.clients?.name,
          },
          mode: item.action_data?.mode || item.mode || 'outbound',
        };
      });

      // Separate active and archived actions
      const activeActions = transformedData.filter(
        (action) => action.status !== 'archived',
      );
      const archivedActionsData = transformedData.filter(
        (action) => action.status === 'archived',
      );

      // Append new actions to existing ones
      setActions((prev) => {
        const existingIds = new Set(prev.map((a) => a.id));
        const newActions = activeActions.filter((a) => !existingIds.has(a.id));
        return [...prev, ...newActions];
      });
      setArchivedActions((prev) => {
        const existingIds = new Set(prev.map((a) => a.id));
        const newArchivedActions = archivedActionsData.filter(
          (a) => !existingIds.has(a.id),
        );
        return [...prev, ...newArchivedActions];
      });
    } catch (error) {
      console.error('Error loading more actions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load more actions',
        variant: 'destructive',
      });
    } finally {
      setLoadingMore(false);
    }
  };

  // Get current actions
  const currentActions = viewMode === 'active' ? actions : archivedActions;

  // Check if any filters are active (for UI indication)
  const hasActiveFilters =
    modeFilter !== 'all' ||
    clientFilter !== 'all' ||
    searchQuery.trim() ||
    showProcessed;

  const resetFilters = () => {
    setModeFilter('all');
    setClientFilter('all');
    setSearchQuery('');
    setShowProcessed(false);
  };

  // Archive functionality
  const initiateArchive = (
    contactKey: string,
    contactActions: CallAction[],
  ) => {
    const contactIntelligence = extractContactIntelligence(contactActions);
    setContactToArchive({
      contactKey,
      name: contactIntelligence.name,
      actionIds: contactActions.map((a) => a.id),
    });
    setArchiveModalOpen(true);
  };

  const confirmArchive = async () => {
    if (!contactToArchive) {
      return;
    }

    try {
      const now = new Date();
      const archiveUntil = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from now

      const { error } = await supabase
        .from('call_actions')
        .update({
          status: 'archived',
          completed_at: now.toISOString(),
          archived_at: now.toISOString(),
          archived_until: archiveUntil.toISOString(),
          processed: true,
          processed_by: user?.id,
        })
        .in('id', contactToArchive.actionIds);

      if (error) throw error;

      // Move actions from active to archived in local state
      const archivedActionsToMove = actions.filter((action) =>
        contactToArchive.actionIds.includes(action.id),
      );

      const updatedArchivedActions = archivedActionsToMove.map((action) => ({
        ...action,
        status: 'archived' as const,
        completed_at: now.toISOString(),
        archived_at: now.toISOString(),
        archived_until: archiveUntil.toISOString(),
        processed: true,
      }));

      setActions((prev) =>
        prev.filter(
          (action) => !contactToArchive.actionIds.includes(action.id),
        ),
      );
      setArchivedActions((prev) => [...prev, ...updatedArchivedActions]);

      toast({
        title: 'Success',
        description: `Contact archived. Will be deleted automatically in 30 days.`,
      });
    } catch (error) {
      console.error('Error archiving contact:', error);
      toast({
        title: 'Error',
        description: 'Failed to archive contact',
        variant: 'destructive',
      });
    }

    setContactToArchive(null);
  };

  const deleteArchivedContact = async (
    contactKey: string,
    contactActions: CallAction[],
  ) => {
    try {
      const actionIds = contactActions.map((a) => a.id);

      const { error } = await supabase
        .from('call_actions')
        .delete()
        .in('id', actionIds);

      if (error) throw error;

      // Remove from local state
      setArchivedActions((prev) =>
        prev.filter((action) => !actionIds.includes(action.id)),
      );

      toast({
        title: 'Success',
        description: 'Archived contact permanently deleted',
      });
    } catch (error) {
      console.error('Error deleting archived contact:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete archived contact',
        variant: 'destructive',
      });
    }
  };

  // Search function
  const matchesSearch = (action: CallAction, query: string): boolean => {
    if (!query) return true;

    const searchLower = query.toLowerCase();
    const searchFields = [
      action.caller_name,
      action.action_data?.caller_name,
      action.action_data?.phone_number,
      action.action_data?.email,
      action.phrase,
      action.snippet,
      action.campaign?.name,
      action.campaign_id,
      action.prospect_id,
      action.call_id,
      action.id,
    ];

    return searchFields.some(
      (field) => field && String(field).toLowerCase().includes(searchLower),
    );
  };

  // Filter actions without mode filter first (for global counts)
  const preModeFilteredActions = currentActions
    .filter((action) => {
      // For archive view, only show completed/archived actions
      if (viewMode === 'archive') {
        return action.status === 'archived' || action.processed;
      }

      // For active view, always include non-archived actions regardless of processed state.
      // Processed items stay visible with strikethrough so they can be reviewed and unchecked.
      // Contacts only leave the active view when explicitly archived via "Mark as Done".
      if (viewMode === 'active') {
        if (action.status === 'archived') return false;
        return true;
      }

      return true;
    })
    .filter((action) => {
      // Client filter
      if (clientFilter !== 'all') {
        if (clientFilter === 'unassigned' && action.campaign?.client_id)
          return false;
        if (
          clientFilter !== 'unassigned' &&
          action.campaign?.client_id !== clientFilter
        )
          return false;
      }

      // Search filter
      return matchesSearch(action, searchQuery);
    });

  // Compute global counts (ignoring modeFilter)
  const allContacts = useGroupedActions(preModeFilteredActions);
  const globalAllCount = Object.keys(allContacts).length;

  const outboundActions = preModeFilteredActions.filter(
    (a) => a.mode !== 'inbound',
  );
  const outboundContacts = useGroupedActions(outboundActions);
  const globalOutboundCount = Object.keys(outboundContacts).length;

  const inboundActions = preModeFilteredActions.filter(
    (a) => a.mode === 'inbound',
  );
  const inboundContacts = useGroupedActions(inboundActions);
  const globalInboundCount = Object.keys(inboundContacts).length;

  // Now apply mode filter for the displayed list
  const filteredAndSearchedActions = preModeFilteredActions
    .filter((action) => {
      // Mode filter
      if (modeFilter === 'inbound' && action.mode !== 'inbound') return false;
      if (modeFilter === 'outbound' && action.mode === 'inbound') return false;
      return true;
    })
    .sort((a, b) => {
      if (viewMode === 'archive') {
        // For archive, sort by completion date (newest first)
        const aDate = a.completed_at || a.archived_at || a.created_at;
        const bDate = b.completed_at || b.archived_at || b.created_at;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      }

      if (sortMode === 'due_date') {
        // Sort by due date - earliest first, then by created_at
        const aDeadline = getActionDeadlineDate(
          a.type,
          a.created_at,
          a.action_data?.due_at,
        );
        const bDeadline = getActionDeadlineDate(
          b.type,
          b.created_at,
          b.action_data?.due_at,
        );

        if (aDeadline && bDeadline) {
          return new Date(aDeadline).getTime() - new Date(bDeadline).getTime();
        }
        if (aDeadline) return -1;
        if (bDeadline) return 1;

        // Fallback to creation date if no due dates
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      } else {
        // Sort by creation date - newest first
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
    });

  // Group filtered actions by contact
  const groupedContacts = useGroupedActions(filteredAndSearchedActions);
  const contactKeys = Object.keys(groupedContacts);

  // Archive counts
  const archivedAllContacts = useGroupedActions(archivedActions);
  const archivedContactCount = Object.keys(archivedAllContacts).length;

  const toggleActionProcessed = async (actionId: string) => {
    try {
      const action = actions.find((a) => a.id === actionId);
      if (!action) return;

      const { error } = await supabase
        .from('call_actions')
        .update({
          processed: !action.processed,
          processed_by: action.processed ? null : user?.id,
        })
        .eq('id', actionId);

      if (error) throw error;

      // Update local state without removing the action
      setActions((prev) =>
        prev.map((a) =>
          a.id === actionId ? { ...a, processed: !a.processed } : a,
        ),
      );

      toast({
        title: 'Success',
        description: `Action ${
          action.processed ? 'unmarked' : 'marked'
        } as done`,
      });
    } catch (error) {
      console.error('Error toggling action status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update action status',
        variant: 'destructive',
      });
    }
  };

  const updateActionDueDate = useCallback(
    async (actionId: string, dueAtISO: string) => {
      try {
        const action = actions.find((a) => a.id === actionId);
        if (!action) return;

        // Update action_data.due_at in the database
        const { error } = await supabase
          .from('call_actions')
          .update({
            action_data: {
              ...action.action_data,
              due_at: dueAtISO,
            },
          })
          .eq('id', actionId);

        if (error) throw error;

        // Update local state to reflect the change
        setActions((prev) =>
          prev.map((a) =>
            a.id === actionId
              ? {
                  ...a,
                  action_data: {
                    ...a.action_data,
                    due_at: dueAtISO,
                  },
                }
              : a,
          ),
        );
      } catch (error) {
        console.error('[Actions.tsx] Error updating due date:', error);
        throw error;
      }
    },
    [actions],
  );

  const markAsDone = async (actionId: string) => {
    try {
      const { error } = await supabase
        .from('call_actions')
        .update({ processed: true, processed_by: user?.id })
        .eq('id', actionId);

      if (error) throw error;

      // Update local state to mark as processed but keep the action
      setActions((prev) =>
        prev.map((action) =>
          action.id === actionId ? { ...action, processed: true } : action,
        ),
      );

      toast({
        title: 'Success',
        description: 'Action marked as done',
      });
    } catch (error) {
      console.error('Error marking action as done:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark action as done',
        variant: 'destructive',
      });
    }
  };

  // Helper function to extract business context from inbound calls
  const extractInboundContext = (phrase: string | null, actionData: any) => {
    if (!phrase) return '';

    const contextPatterns = [
      {
        pattern: /construction|contractor|building/i,
        industry: 'Construction',
      },
      { pattern: /medical|healthcare|doctor|clinic/i, industry: 'Healthcare' },
      { pattern: /restaurant|food|catering/i, industry: 'Food Service' },
      { pattern: /legal|law|attorney|lawyer/i, industry: 'Legal' },
      { pattern: /real estate|property|homes/i, industry: 'Real Estate' },
      { pattern: /tech|software|IT|technology/i, industry: 'Technology' },
      { pattern: /retail|store|shop/i, industry: 'Retail' },
      { pattern: /insurance|policy/i, industry: 'Insurance' },
    ];

    const inquiryPatterns = [
      { pattern: /billing|invoice|payment/i, type: 'Billing inquiry' },
      { pattern: /appointment|schedule|booking/i, type: 'Appointment request' },
      { pattern: /quote|pricing|estimate/i, type: 'Quote request' },
      { pattern: /support|help|issue|problem/i, type: 'Support inquiry' },
      { pattern: /information|details|about/i, type: 'Information request' },
    ];

    let industry = '';
    let inquiryType = '';

    for (const ctx of contextPatterns) {
      if (ctx.pattern.test(phrase)) {
        industry = ctx.industry;
        break;
      }
    }

    for (const inq of inquiryPatterns) {
      if (inq.pattern.test(phrase)) {
        inquiryType = inq.type;
        break;
      }
    }

    if (industry && inquiryType) {
      return `${industry} - ${inquiryType}`;
    } else if (inquiryType) {
      return inquiryType;
    } else if (industry) {
      return `${industry} company`;
    }

    return 'Business inquiry';
  };

  if (loading) {
    return (
      <div className='container mx-auto p-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-muted rounded w-1/3 mb-6'></div>
          <div className='space-y-4'>
            {[1, 2, 3].map((i) => (
              <div key={i} className='h-32 bg-muted rounded'></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container mx-auto p-6'>
      <div className='mb-6 flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Action Center</h1>
          <p className='text-muted-foreground'>
            Review and manage follow-up actions from your contacts
            {searchQuery && (
              <span className='block text-sm mt-1'>
                Showing {contactKeys.length} contact
                {contactKeys.length !== 1 ? 's' : ''} matching "{searchQuery}"
              </span>
            )}
          </p>
        </div>

        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => fetchActions(true)}
            disabled={loading}
            className='gap-2'
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {hasVideo && (
            <TrainingVideoModal
              trigger={
                <Button variant='outline' size='sm'>
                  <PlayCircle className='h-4 w-4 mr-2' />
                  {displayTitle || 'View Training'}
                </Button>
              }
              locationKey='actions'
            />
          )}
        </div>
      </div>

      {/* Search and Sort Controls */}
      <div className='mb-4 flex gap-4'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4' />
          <Input
            placeholder='Search by name, phone, email, campaign ID...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='pl-9'
          />
        </div>
        <Select
          value={sortMode}
          onValueChange={(value: 'due_date' | 'created_at') =>
            setSortMode(value)
          }
        >
          <SelectTrigger className='w-48'>
            <SelectValue placeholder='Sort by...' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='due_date'>Due Date (Earliest)</SelectItem>
            <SelectItem value='created_at'>Created (Newest)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* View Mode Toggle (Active/Archive) */}
      <div
        className={`flex gap-2 ${
          isClientPortal ? 'container mx-auto px-4' : 'mb-4'
        }`}
      >
        <Button
          variant={viewMode === 'active' ? 'default' : 'outline'}
          size='sm'
          onClick={() => setViewMode('active')}
          className='gap-1.5'
        >
          Active Actions
          <Badge variant='secondary' className='ml-1'>
            {contactKeys.length}
          </Badge>
        </Button>
        <Button
          variant={viewMode === 'archive' ? 'default' : 'outline'}
          size='sm'
          onClick={() => setViewMode('archive')}
          className='gap-1.5'
        >
          <Archive className='h-3 w-3' />
          Archive
          <Badge variant='secondary' className='ml-1'>
            {archivedContactCount}
          </Badge>
        </Button>
      </div>

      {/* Filter Pills */}
      <div
        className={`flex flex-wrap gap-2 ${
          isClientPortal ? 'container mx-auto px-4 mb-6' : 'mb-6'
        }`}
      >
        <Button
          variant={modeFilter === 'all' ? 'default' : 'outline'}
          size='sm'
          onClick={() => setModeFilter('all')}
          className='gap-1.5'
        >
          All Contacts
          <Badge variant='secondary' className='ml-1'>
            {globalAllCount}
          </Badge>
        </Button>
        <Button
          variant={modeFilter === 'outbound' ? 'default' : 'outline'}
          size='sm'
          onClick={() => setModeFilter('outbound')}
          className='gap-1.5'
        >
          <PhoneOutgoing className='h-3 w-3' />
          Outbound
          <Badge variant='secondary' className='ml-1'>
            {globalOutboundCount}
          </Badge>
        </Button>
        {enableInboundMode && (
          <Button
            variant={modeFilter === 'inbound' ? 'default' : 'outline'}
            size='sm'
            onClick={() => setModeFilter('inbound')}
            className='gap-1.5'
          >
            <PhoneIncoming className='h-3 w-3' />
            Inbound
            <Badge variant='secondary' className='ml-1'>
              {globalInboundCount}
            </Badge>
          </Button>
        )}

        {/* Client Filter - hidden in client portal mode */}
        {!isClientPortal && (
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className='w-48'>
              <SelectValue placeholder='Filter by client...' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Clients</SelectItem>
              <SelectItem value='unassigned'>Unassigned</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Show processed toggle - only in active mode */}
        {viewMode === 'active' && (
          <Button
            variant={showProcessed ? 'default' : 'outline'}
            size='sm'
            onClick={() => setShowProcessed(!showProcessed)}
            className='gap-1.5'
          >
            <CheckCircle className='h-3 w-3' />
            Show Completed
          </Button>
        )}

        {/* Reset filters button */}
        {hasActiveFilters && (
          <Button
            variant='ghost'
            size='sm'
            onClick={resetFilters}
            className='gap-1.5 text-destructive'
          >
            Reset filters
          </Button>
        )}

        {/* Active filters indicator */}
        {hasActiveFilters && (
          <Badge variant='destructive' className='gap-1'>
            Filters active
          </Badge>
        )}
      </div>

      {contactKeys.length === 0 ? (
        <div className={isClientPortal ? 'container mx-auto px-4' : ''}>
          <Card>
            <CardContent className='flex flex-col items-center justify-center py-16'>
              <CheckCircle className='h-16 w-16 text-muted-foreground mb-4' />
              <h3 className='text-lg font-semibold mb-2'>
                {viewMode === 'archive'
                  ? 'No archived contacts'
                  : 'All caught up!'}
              </h3>
              <p className='text-muted-foreground text-center'>
                {viewMode === 'archive'
                  ? 'No archived contacts found. Contacts will appear here after being marked as done.'
                  : currentActions.length === 0
                    ? 'No pending actions at the moment. Actions will appear here when prospects give consent for callbacks or show interest in appointments.'
                    : `No ${
                        modeFilter === 'all' ? '' : modeFilter
                      } contacts match your filter.`}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div
          className={`space-y-4 ${
            isClientPortal ? 'container mx-auto px-4' : ''
          }`}
        >
          {contactKeys.map((contactKey) => {
            const contactActions = groupedContacts[contactKey];
            return (
              <ContactCard
                key={contactKey}
                contactKey={contactKey}
                actions={
                  viewMode === 'archive'
                    ? contactActions
                    : contactActions.filter((a) => !a.processed)
                }
                processedActions={
                  viewMode === 'archive'
                    ? []
                    : contactActions.filter((a) => a.processed)
                }
                onMarkActionDone={
                  viewMode === 'archive' ? undefined : markAsDone
                }
                onToggleActionProcessed={
                  viewMode === 'archive'
                    ? undefined
                    : debouncedToggleActionProcessed
                }
                onUpdateDueDate={
                  viewMode === 'archive' ? undefined : updateActionDueDate
                }
                onArchive={
                  viewMode === 'archive'
                    ? undefined
                    : () => initiateArchive(contactKey, contactActions)
                }
                onDelete={
                  viewMode === 'archive'
                    ? () => deleteArchivedContact(contactKey, contactActions)
                    : undefined
                }
                isArchived={viewMode === 'archive'}
                prospectLists={prospectLists}
              />
            );
          })}

          {/* Load More Button */}
          {hasMoreCalls && viewMode === 'active' && (
            <div className='flex justify-center py-6'>
              <Button
                onClick={loadMoreActions}
                disabled={loadingMore}
                variant='outline'
                size='lg'
                className='gap-2'
              >
                {loadingMore ? (
                  <>
                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
                    Loading more calls...
                  </>
                ) : (
                  'Load More Calls'
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Archive Confirmation Modal */}
      <ArchiveConfirmationModal
        open={archiveModalOpen}
        onOpenChange={setArchiveModalOpen}
        onConfirm={confirmArchive}
        contactName={contactToArchive?.name}
        actionCount={contactToArchive?.actionIds.length || 0}
      />
    </div>
  );
}
